import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowDown,
  Phone,
  Mail,
  Check,
  Search,
  Wrench,
  Split,
  ShieldCheck,
  Building2,
  HardHat,
  Building,
  Landmark,
  FileCheck2,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BRAND, IMG, PROJECT_TYPES, CASE_STUDIES } from "@/lib/site";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  company: "",
  email: "",
  phone: "",
  location: "",
  project_type: "",
  message: "",
};

const AUDIENCES = [
  { icon: Building2, label: "Structural Engineers" },
  { icon: HardHat, label: "Main Contractors" },
  { icon: Building, label: "Commercial Property Owners" },
  { icon: ShieldCheck, label: "Insurers & Loss Adjusters" },
  { icon: Landmark, label: "Local Authorities" },
];

const CORE_SERVICES = [
  {
    icon: Search,
    title: "Structural Diagnostics",
    body: "Cover meter, half-cell, hammer-tap and defect mapping. We tell you the mechanism before we tell you the fix.",
  },
  {
    icon: Wrench,
    title: "Reinforcement Corrosion Repair",
    body: "Breakout · SA 2½ clean · zinc-rich primer · EN 1504-3 R4 mortar reinstatement. To spec, to standard.",
  },
  {
    icon: Split,
    title: "Crack Injection & Stitching",
    body: "Low-viscosity epoxy for structural cracks. Helifix stainless helical bars for restrained movement.",
  },
  {
    icon: ShieldCheck,
    title: "Anti-Carbonation Coatings",
    body: "Dekguard S, S200 and Elastic — EN 1504-2 coatings applied after repair to extend service life.",
  },
];

const TRUST = [
  { n: "EN 1504", label: "Compliant methodology" },
  { n: "< 24h", label: "Weekday enquiry response" },
  { n: "UK-wide", label: "Nationwide site coverage" },
  { n: "R3 / R4", label: "Structural repair mortars" },
];

export default function Quote() {
  const navigate = useNavigate();
  const [form, setForm] = React.useState(initial);
  const [submitting, setSubmitting] = React.useState(false);
  const [errors, setErrors] = React.useState({});
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please tell us your name";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Enter a valid email";
    if (!form.location.trim()) e.location = "Please tell us where the site is";
    if (!form.message.trim() || form.message.trim().length < 10)
      e.message = "A short description helps us respond faster (min. 10 characters)";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please check the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });
      await axios.post(`${API}/enquiries`, payload);
      // Google Ads conversion (no-op if ad_storage consent not granted).
      // GA4 generate_lead fires on /thank-you mount.
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        try {
          window.gtag("event", "conversion", {
            send_to: "AW-612532606",
            value: 50.0,
            currency: "GBP",
          });
        } catch { /* ignore */ }
      }
      setForm(initial);
      navigate("/thank-you");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputBase =
    "w-full bg-white border border-zinc-300 px-4 py-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-[#C2410C] focus:ring-1 focus:ring-[#C2410C] transition-colors";

  const scrollToForm = () => {
    const el = document.getElementById("survey-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FDFDFD] text-zinc-900" data-testid="quote-page">
      <SEO
        title="Book a Free Concrete Repair Survey"
        description="EN 1504-compliant concrete repair for engineers, contractors and commercial property owners. Book a free site survey. Nationwide UK coverage."
        noindex
      />

      {/* ============ MINIMAL HEADER ============ */}
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between gap-3">
          <img
            src={BRAND.logoUrl}
            alt={BRAND.name}
            className="h-12 md:h-14 w-auto"
            fetchPriority="high"
            data-testid="quote-logo"
          />
          <div className="flex items-center gap-4 md:gap-6">
            <a
              href={`mailto:${BRAND.email}`}
              className="hidden md:flex items-center gap-2 text-sm text-zinc-900 hover:text-[#C2410C] transition-colors"
              data-testid="quote-header-email"
            >
              <Mail size={16} strokeWidth={2} className="text-[#C2410C]" />
              <span className="font-mono">{BRAND.email}</span>
            </a>
            <a
              href={`mailto:${BRAND.email}`}
              className="md:hidden flex items-center text-zinc-900 hover:text-[#C2410C] transition-colors"
              aria-label={`Email ${BRAND.email}`}
              data-testid="quote-header-email-mobile"
            >
              <Mail size={18} strokeWidth={2} className="text-[#C2410C]" />
            </a>
            <a
              href={`tel:${BRAND.phoneDial}`}
              className="flex items-center gap-2 text-sm md:text-base text-zinc-900 hover:text-[#C2410C] transition-colors"
              data-testid="quote-header-phone"
            >
              <Phone size={16} strokeWidth={2} className="text-[#C2410C]" />
              <span className="font-mono">{BRAND.phone}</span>
            </a>
          </div>
        </div>
      </header>

      {/* ============ HERO + FORM ============ */}
      <section className="relative border-b border-zinc-900 bg-zinc-950">
        <div className="absolute inset-0">
          <img
            src={IMG.rebarCloseup}
            alt="Reinforcement corrosion inside broken-out concrete"
            fetchPriority="high"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/95 via-zinc-950/80 to-zinc-950/40" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 pt-14 pb-20 md:pt-20 md:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
            {/* Left: pitch */}
            <div className="lg:col-span-7 text-white">
              <div className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.24em] text-[#D94515]">
                <span className="inline-block h-2 w-2 bg-[#D94515]" />
                <span>// SPEC · 01 · UK CONCRETE REPAIR SPECIALISTS</span>
              </div>

              <h1 className="font-display text-[40px] leading-[0.95] sm:text-5xl md:text-6xl lg:text-7xl font-black uppercase tracking-tight mt-6">
                Concrete repair,<br />
                <span className="text-[#D94515]">diagnosed properly.</span>
              </h1>

              <p className="mt-8 max-w-xl text-base md:text-lg text-zinc-200 leading-relaxed">
                EN 1504-compliant investigation and repair of reinforced concrete
                structures. We work with structural engineers, main contractors,
                commercial property owners and insurers across the United Kingdom.
              </p>

              <ul className="mt-8 space-y-3 max-w-xl">
                {[
                  "EN 1504 compliant methodology, R3 / R4 structural mortars",
                  "Diagnostic-first — we find the mechanism before we quote the fix",
                  "Nationwide UK coverage — car parks, façades, marine, industrial",
                  "Costed repair specification you can put in front of an engineer",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <Check size={18} strokeWidth={2.5} className="mt-0.5 text-[#D94515] flex-shrink-0" />
                    <span className="text-zinc-100">{t}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-10">
                <div
                  className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight text-white"
                  data-testid="quote-hero-instruction"
                >
                  Book a free survey below
                </div>
                {/* Mobile-only scroll-to-form link (desktop already shows form to the right) */}
                <button
                  type="button"
                  onClick={scrollToForm}
                  className="md:hidden mt-5 inline-flex items-center gap-2 text-[#D94515] font-mono text-sm uppercase tracking-[0.16em] border-b-2 border-[#D94515] pb-1 hover:text-white hover:border-white transition-colors"
                  data-testid="quote-hero-scroll-to-form"
                  aria-label="Scroll to survey form"
                >
                  Take me to the form
                  <ArrowDown size={16} strokeWidth={2.5} className="animate-bounce" />
                </button>
                <div className="mt-6 md:mt-4 text-sm text-zinc-300 font-mono uppercase tracking-[0.12em] leading-relaxed">
                  Email us at{" "}
                  <a
                    href={`mailto:${BRAND.email}`}
                    className="text-white underline decoration-[#D94515] decoration-2 underline-offset-4 hover:text-[#D94515] transition-colors normal-case"
                    data-testid="quote-hero-email"
                  >
                    {BRAND.email}
                  </a>{" "}
                  or call us on{" "}
                  <a
                    href={`tel:${BRAND.phoneDial}`}
                    className="text-white underline decoration-[#D94515] decoration-2 underline-offset-4 hover:text-[#D94515] transition-colors whitespace-nowrap"
                    data-testid="quote-hero-call"
                  >
                    {BRAND.phone}
                  </a>
                </div>
              </div>
            </div>

            {/* Right: form */}
            <div className="lg:col-span-5">
              <form
                id="survey-form"
                onSubmit={onSubmit}
                className="border border-zinc-100 p-6 md:p-8 bg-white relative shadow-2xl"
                data-testid="quote-form"
              >
                <div className="tech-label text-[#C2410C]">// FREE SITE SURVEY</div>
                <h2 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight mt-2 leading-tight">
                  Tell us about the structure
                </h2>
                <p className="text-xs text-zinc-500 mt-2">Reply within one working day.</p>

                <div className="rule-line my-6 bg-zinc-200" />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Name" required error={errors.name}>
                    <input
                      type="text"
                      className={inputBase}
                      value={form.name}
                      onChange={(e) => set("name", e.target.value)}
                      placeholder="Your full name"
                      data-testid="quote-input-name"
                    />
                  </Field>
                  <Field label="Company">
                    <input
                      type="text"
                      className={inputBase}
                      value={form.company}
                      onChange={(e) => set("company", e.target.value)}
                      placeholder="Optional"
                      data-testid="quote-input-company"
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <Field label="Email" required error={errors.email}>
                    <input
                      type="email"
                      className={inputBase}
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                      placeholder="you@example.com"
                      data-testid="quote-input-email"
                    />
                  </Field>
                  <Field label="Phone">
                    <input
                      type="tel"
                      className={inputBase}
                      value={form.phone}
                      onChange={(e) => set("phone", e.target.value)}
                      placeholder="Optional"
                      data-testid="quote-input-phone"
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <Field label="Site location" required error={errors.location}>
                    <input
                      type="text"
                      className={inputBase}
                      value={form.location}
                      onChange={(e) => set("location", e.target.value)}
                      placeholder="e.g. Manchester city centre"
                      data-testid="quote-input-location"
                    />
                  </Field>
                  <Field label="Project type">
                    <Select
                      value={form.project_type}
                      onValueChange={(v) => set("project_type", v)}
                    >
                      <SelectTrigger
                        className="w-full rounded-none border-zinc-300 h-[46px] focus:ring-[#C2410C] focus:ring-1 data-[state=open]:border-[#C2410C]"
                        data-testid="quote-select-project-type"
                      >
                        <SelectValue placeholder="Select a type" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {PROJECT_TYPES.map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <div className="mt-4">
                  <Field label="What are you seeing?" required error={errors.message}>
                    <textarea
                      rows={4}
                      className={inputBase + " resize-y"}
                      value={form.message}
                      onChange={(e) => set("message", e.target.value)}
                      placeholder="Spalling, staining, cracks, exposed rebar, age of structure…"
                      data-testid="quote-input-message"
                    />
                  </Field>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full justify-center mt-6 disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="quote-submit-btn"
                >
                  {submitting ? "Sending…" : "Request survey"}{" "}
                  <ArrowUpRight className="arrow" size={14} strokeWidth={2.5} />
                </button>

                <p className="text-[11px] text-zinc-500 mt-4 font-mono uppercase tracking-[0.14em] text-center">
                  // No obligation · No pushy follow-up
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ============ WHO WE WORK WITH ============ */}
      <section className="border-b border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div className="tech-label">// Who we work with</div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tight mt-4 max-w-3xl">
            Concrete repair for people who know it matters.
          </h2>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-6" data-testid="quote-audiences">
            {AUDIENCES.map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="border border-zinc-200 p-6 flex flex-col items-start gap-4 hover:border-zinc-900 transition-colors"
              >
                <Icon size={22} strokeWidth={2} className="text-[#C2410C]" />
                <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-700 leading-tight">
                  {label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ WHAT WE DO ============ */}
      <section className="border-b border-zinc-200 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
            <div className="md:col-span-5">
              <div className="tech-label">// What we do</div>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tight mt-4">
                Four core capabilities. One repair discipline.
              </h2>
            </div>
            <p className="md:col-span-7 text-zinc-700 leading-relaxed md:pt-16 text-base md:text-lg">
              Every job starts with a diagnostic-first survey and ends with a
              costed, EN 1504-compliant repair specification. No guessing, no
              generic quotes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="quote-services">
            {CORE_SERVICES.map(({ icon: Icon, title, body }, i) => (
              <div
                key={title}
                className="border border-zinc-900 p-6 bg-white hover:bg-zinc-950 hover:text-white group transition-colors"
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 group-hover:text-zinc-400">
                  // 0{i + 1}
                </div>
                <Icon size={24} strokeWidth={2} className="text-[#C2410C] mt-6" />
                <h3 className="font-display text-xl font-bold uppercase tracking-tight mt-4 leading-tight">
                  {title}
                </h3>
                <p className="text-sm text-zinc-700 group-hover:text-zinc-300 mt-3 leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TRUST STRIP ============ */}
      <section className="border-b border-zinc-900 bg-zinc-950 text-white">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 divide-x divide-zinc-800">
          {TRUST.map(({ n, label }) => (
            <div key={label} className="p-8 md:p-10">
              <div className="font-display text-3xl md:text-4xl lg:text-5xl font-black leading-none">
                {n}
              </div>
              <div className="tech-label mt-3 text-zinc-400">{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ RECENT PROJECTS ============ */}
      <section className="border-b border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-24">
          <div className="tech-label">// Recent projects</div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tight mt-4 max-w-3xl">
            Diagnosis, in the field.
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="quote-cases">
            {CASE_STUDIES.slice(0, 3).map((cs) => (
              <div key={cs.id} className="border border-zinc-200 flex flex-col">
                <div className="aspect-[4/3] bg-zinc-100 overflow-hidden">
                  <img
                    src={IMG[cs.image]}
                    alt={cs.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    // {cs.ref} · {cs.location} · {cs.year}
                  </div>
                  <h3 className="font-display text-lg font-bold uppercase tracking-tight mt-3 leading-tight">
                    {cs.title}
                  </h3>
                  <p className="text-sm text-zinc-600 mt-3 leading-relaxed line-clamp-3">
                    {cs.problem}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="border-b border-zinc-200 bg-[#F7F5F1]">
        <div className="max-w-4xl mx-auto px-6 py-20 md:py-28 text-center">
          <FileCheck2 size={40} strokeWidth={1.5} className="text-[#C2410C] mx-auto" />
          <div className="tech-label mt-6">// Ready when you are</div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tight mt-4 leading-tight">
            Get a costed repair specification.
          </h2>
          <p className="mt-6 text-zinc-700 max-w-xl mx-auto leading-relaxed">
            Send us photos and a short description. We&apos;ll come back with a
            diagnostic view and a route to repair.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <button
              type="button"
              onClick={scrollToForm}
              className="btn-primary"
              data-testid="quote-final-cta"
            >
              Book a free survey <ArrowUpRight className="arrow" size={14} strokeWidth={2.5} />
            </button>
            <a
              href={`tel:${BRAND.phoneDial}`}
              className="text-zinc-900 hover:text-[#C2410C] transition-colors font-mono text-sm tracking-[0.14em]"
              data-testid="quote-final-call"
            >
              or call {BRAND.phone}
            </a>
          </div>
        </div>
      </section>

      {/* ============ MINIMAL FOOTER ============ */}
      <footer className="bg-zinc-950 text-zinc-400 mt-auto">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 text-sm">
          <div>
            <div className="font-mono uppercase tracking-[0.18em] text-white">{BRAND.name}</div>
            <div className="mt-2 text-xs">
              {BRAND.address.line1}, {BRAND.address.line2}, {BRAND.address.postcode}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-6 text-xs font-mono uppercase tracking-[0.14em]">
            <a href={`mailto:${BRAND.email}`} className="hover:text-white transition-colors">{BRAND.email}</a>
            <a href={`tel:${BRAND.phoneDial}`} className="hover:text-white transition-colors">{BRAND.phone}</a>
            <a href="/privacy" className="hover:text-white transition-colors">Privacy</a>
            <a href="/cookies" className="hover:text-white transition-colors">Cookies</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500">
        {label} {required && <span className="text-[#C2410C]">*</span>}
      </span>
      <div className="mt-2">{children}</div>
      {error && (
        <span className="mt-1.5 block text-xs text-red-600" data-testid="quote-field-error">
          {error}
        </span>
      )}
    </label>
  );
}
